import React from "react";

// // // //

// Set a tolerance level to ignore minor changes in height on mobile
// NOTE - this is necessary to handle erroneous window resize events in iOS devices.
// The issue of iOS triggering resize events on scrolling is a known problem.
// This is because of the way iOS Safari/Chrome handles the address bar and other
// UI elements, which can cause the inner height of the window
// to change during scroll events.
const HEIGHT_TOLERANCE = 100;

// How long resize events must be quiet before subscribers are told the resize ended
const RESIZE_END_DEBOUNCE_MS = 250;

interface WindowDimensions {
    windowWidth: number;
    windowHeight: number;
}

interface ResizeSubscriber {
    onResizeStart: () => void;
    onResizeEnd: (dimensions: WindowDimensions) => void;
}

// Single shared window listener for every WindowResizeObserver on the page.
// Subscribers are notified synchronously from one place so React batches all
// of their state updates into a single commit, instead of each instance
// running its own debounce timers and committing at staggered times.
const subscribers = new Set<ResizeSubscriber>();
let isResizing = false;
let lastWidth = 0;
let lastHeight = 0;
let resizeEndTimer: ReturnType<typeof setTimeout> | null = null;

function handleWindowResize() {
    const newWidth = window.innerWidth;
    const newHeight = window.innerHeight;

    // Short-circuit if window dimensions haven't changed significantly
    if (
        newWidth === lastWidth &&
        Math.abs(newHeight - lastHeight) < HEIGHT_TOLERANCE
    ) {
        return;
    }

    lastWidth = newWidth;
    lastHeight = newHeight;

    // Leading edge: notify on the FIRST significant resize event so heavy
    // children are swapped for placeholders before the drag continues,
    // rather than after a trailing debounce that keeps getting pushed back
    // while events stream in.
    if (!isResizing) {
        isResizing = true;
        subscribers.forEach((subscriber) => subscriber.onResizeStart());
    }

    // Trailing edge: resize is considered over once events go quiet
    if (resizeEndTimer) {
        clearTimeout(resizeEndTimer);
    }
    resizeEndTimer = setTimeout(() => {
        resizeEndTimer = null;
        isResizing = false;
        subscribers.forEach((subscriber) =>
            subscriber.onResizeEnd({
                windowWidth: lastWidth,
                windowHeight: lastHeight,
            }),
        );
    }, RESIZE_END_DEBOUNCE_MS);
}

function subscribeToWindowResize(subscriber: ResizeSubscriber): () => void {
    if (subscribers.size === 0) {
        lastWidth = window.innerWidth;
        lastHeight = window.innerHeight;
        window.addEventListener("resize", handleWindowResize);
    }
    subscribers.add(subscriber);

    return () => {
        subscribers.delete(subscriber);
        if (subscribers.size === 0) {
            window.removeEventListener("resize", handleWindowResize);
            if (resizeEndTimer) {
                clearTimeout(resizeEndTimer);
                resizeEndTimer = null;
            }
            isResizing = false;
        }
    };
}

// // // //

export interface WindowResizeObserverProps {
    debug?: boolean;
    defaultPlaceholderHeight?: number;
    delay?: number;
    loadingPlaceholder?: React.ReactNode | (() => React.ReactNode);
}

/**
 * WindowResizeObserver
 * Hides props.children behind a lightweight placeholder while the window is
 * being resized, and re-mounts them once resizing settles (plus optional delay)
 */
export function WindowResizeObserver(
    props: WindowResizeObserverProps & { children: React.ReactNode },
) {
    const { debug, delay = 0, loadingPlaceholder = null } = props;

    const [shouldRender, setShouldRender] = React.useState(true);
    const [dimensions, setDimensions] = React.useState<WindowDimensions>({
        windowWidth: 0,
        windowHeight: 0,
    });

    React.useEffect(() => {
        // Initial dimensions must be read post-mount: reading window during
        // render would produce SSR/hydration mismatches in the debug output
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDimensions({
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
        });

        let delayTimer: ReturnType<typeof setTimeout> | null = null;

        const unsubscribe = subscribeToWindowResize({
            onResizeStart: () => {
                if (delayTimer) {
                    clearTimeout(delayTimer);
                    delayTimer = null;
                }
                setShouldRender(false);
            },
            onResizeEnd: (newDimensions) => {
                const show = () => {
                    setDimensions(newDimensions);
                    setShouldRender(true);
                };
                if (delay > 0) {
                    delayTimer = setTimeout(show, delay);
                } else {
                    show();
                }
            },
        });

        return () => {
            unsubscribe();
            if (delayTimer) {
                clearTimeout(delayTimer);
            }
        };
    }, [delay]);

    let resizePlaceholderVal: React.ReactNode | null = null;

    if (
        loadingPlaceholder !== null &&
        typeof loadingPlaceholder === "function"
    ) {
        resizePlaceholderVal = loadingPlaceholder();
    } else if (loadingPlaceholder !== null) {
        resizePlaceholderVal = loadingPlaceholder;
    } else if (props.defaultPlaceholderHeight != null) {
        resizePlaceholderVal = (
            <div
                style={{
                    height: `${props.defaultPlaceholderHeight}px`,
                }}
            />
        );
    }

    return (
        <React.Fragment>
            {debug && (
                <pre>
                    {JSON.stringify(
                        {
                            windowWidth: dimensions.windowWidth,
                            windowHeight: dimensions.windowHeight,
                            shouldRender,
                        },
                        null,
                        4,
                    )}
                </pre>
            )}
            {shouldRender ? props.children : resizePlaceholderVal}
        </React.Fragment>
    );
}
